import { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Landmark, Menu, Search, X } from "lucide-react";
import { API } from "../../lib/api";
import { MeasurePage, PublicationPage, RecordCard, Registry, TrackPage } from "./PublicRecords";
import { CommitteesPage, NoticesPage, SessionPage, SessionsPage } from "./PublicMeetings";
import RequestPage from "./PublicRequest";

const Ctx = createContext(null);
export const usePortal = () => useContext(Ctx);
export const pubDate = (v, withTime) => (v ? new Date(/T/.test(v) && !/Z|[+-]\d\d:\d\d$/.test(v) ? `${v}Z` : /T/.test(v) ? v : `${v}T00:00:00+08:00`)
  .toLocaleString("en-PH", { timeZone: "Asia/Manila", dateStyle: "long", ...(withTime ? { timeStyle: "short" } : {}) }) : "—");

const NAV = [["", "Home", null], ["ordinances", "Ordinances", "ordinances"], ["resolutions", "Resolutions", "resolutions"], ["track", "Track a Measure", "tracking"],
  ["sessions", "Sessions", "sessions"], ["committees", "Committees & Hearings", "committees"], ["publication", "Publication & Effectivity", "publication"],
  ["archive", "Archive", "archive"], ["notices", "Notices", "notices"], ["request", "Request a Document", "requests"]];

export function PHeading({ eyebrow, title, children }) {
  return (
    <div className="mb-8">
      {eyebrow && <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700">{eyebrow}</div>}
      <h1 className="font-serif text-3xl text-[#13294b] sm:text-4xl">{title}</h1>
      {children && <p className="mt-3 max-w-2xl text-sm text-slate-600">{children}</p>}
    </div>
  );
}

function Header({ d }) {
  const [open, setOpen] = useState(false);
  const base = `/public/${d.lgu.code}`;
  const mods = d.portal.modules;
  return (
    <header className="sticky top-0 z-30 border-b border-[#0e1f3a] bg-[#13294b] text-white">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
        <Link to={base} className="flex min-w-0 items-center gap-3" data-testid="public-home-link">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-amber-400 bg-[#0e1f3a]"><Landmark className="h-5 w-5 text-amber-400" /></span>
          <span className="min-w-0"><span className="block truncate font-serif text-base leading-tight" data-testid="public-lgu-name">{d.lgu.name}</span>
            <span className="block truncate text-[11px] text-slate-300">{d.portal.portal_title}</span></span>
        </Link>
        <button className="ml-auto lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="public-menu-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      <nav className={`${open ? "block" : "hidden"} border-t border-white/10 lg:block`} data-testid="public-nav">
        <div className="mx-auto flex max-w-6xl flex-col gap-0.5 px-2 py-2 lg:flex-row lg:flex-wrap">
          {NAV.filter(([, , m]) => !m || mods[m]).map(([p, l]) => (
            <NavLink key={p} to={`${base}/${p}`} end={p === ""} onClick={() => setOpen(false)} data-testid={`public-nav-${p || "home"}`}
              className={({ isActive }) => `rounded px-3 py-1.5 text-[13px] transition-colors ${isActive ? "bg-amber-400 font-semibold text-[#13294b]" : "text-slate-200 hover:bg-white/10"}`}>{l}</NavLink>
          ))}
        </div>
      </nav>
    </header>
  );
}

function Home() {
  const d = usePortal();
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const base = `/public/${d.lgu.code}`;
  return (
    <div>
      <section className="mb-12 grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-end">
        <div>
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700">Republic of the Philippines · {d.lgu.legislative_body}</div>
          <h1 className="font-serif text-4xl leading-tight text-[#13294b] sm:text-5xl lg:text-6xl">Laws and legislative records of {d.lgu.name}</h1>
          <p className="mt-4 max-w-xl text-sm text-slate-600">Search officially released ordinances and resolutions, follow a measure through the Sanggunian, and see posting and effectivity records.</p>
          <form onSubmit={(e) => { e.preventDefault(); navigate(`${base}/search?q=${encodeURIComponent(q)}`); }} className="mt-6 flex max-w-xl gap-2" data-testid="public-home-search">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ordinance number, title or keyword" className="h-12 flex-1 rounded-md border border-slate-300 bg-white px-4 text-sm outline-none focus:border-[#13294b]" data-testid="public-home-search-input" />
            <button className="flex h-12 items-center gap-2 rounded-md bg-[#13294b] px-5 text-sm font-semibold text-white hover:bg-[#0e1f3a]" data-testid="public-home-search-button"><Search className="h-4 w-4" />Search</button>
          </form>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[["Ordinances", d.counts.ordinances], ["Resolutions", d.counts.resolutions], ["Sessions", d.counts.sessions]].map(([l, n]) => (
            <div key={l} className="rounded-md border border-slate-200 bg-white p-4" data-testid={`public-count-${l.toLowerCase()}`}><div className="font-serif text-3xl text-[#13294b]">{n}</div><div className="text-xs text-slate-500">{l} released</div></div>
          ))}
        </div>
      </section>
      <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr]">
        <section>
          <h2 className="mb-4 font-serif text-lg text-[#13294b]">Recently released</h2>
          <div className="space-y-3">{d.latest.length ? d.latest.map((m) => <RecordCard key={m.tracking_no} m={m} />) : <p className="text-sm text-slate-500" data-testid="public-latest-empty">No records have been released yet.</p>}</div>
        </section>
        <aside className="space-y-6">
          <div>
            <h2 className="mb-4 font-serif text-lg text-[#13294b]">Notices</h2>
            {d.notices.length ? d.notices.map((n) => (
              <div key={n.id} className="mb-3 border-l-4 border-amber-400 bg-white p-3 text-sm" data-testid={`public-home-notice-${n.id}`}><div className="font-semibold text-slate-800">{n.title}</div><div className="text-xs text-slate-500">{pubDate(n.published_at)}</div></div>
            )) : <p className="text-sm text-slate-500">No current notices.</p>}
          </div>
          <div className="rounded-md bg-[#13294b] p-5 text-sm text-slate-200">
            <div className="mb-2 font-serif text-base text-white">{d.portal.records_office}</div>
            <p className="mb-3 text-xs">{d.portal.access_instructions}</p>
            {d.portal.contact_email && <div className="text-xs">{d.portal.contact_email}</div>}{d.portal.contact_number && <div className="text-xs">{d.portal.contact_number}</div>}
            {d.portal.modules.requests && <Link to={`${base}/request`} className="mt-4 inline-block rounded bg-amber-400 px-3 py-1.5 text-xs font-semibold text-[#13294b]" data-testid="public-home-request-link">Request a document</Link>}
          </div>
        </aside>
      </div>
    </div>
  );
}

export function usePublic(path, deps = []) {
  const { code } = useParams();
  const [state, setState] = useState({ data: null, error: null });
  useEffect(() => {
    setState({ data: null, error: null });
    pubApi(code).get(path).then((r) => setState({ data: r.data, error: null })).catch((e) => setState({ data: null, error: e.response?.data?.detail || "Unable to load this page." }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, path, ...deps]);
  return state;
}

export const pubApi = (code) => axios.create({ baseURL: `${API}/public/${code}` });

export default function PublicPortal() {
  const { code } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { pubApi(code).get("").then((r) => setD(r.data)).catch((e) => setError(e.response?.data?.detail || "Portal not available")); }, [code]);
  if (error) return <div className="flex min-h-screen items-center justify-center bg-[#f7f4ee] p-6 text-center font-serif text-xl text-[#13294b]" data-testid="public-portal-error">{typeof error === "string" ? error : "Portal not available"}</div>;
  if (!d) return <div className="min-h-screen bg-[#f7f4ee] p-10 text-sm text-slate-500">Loading…</div>;
  return (
    <Ctx.Provider value={d}>
      <div className="min-h-screen bg-[#f7f4ee] text-slate-800" data-testid="public-portal">
        <Header d={d} />
        <main className="mx-auto max-w-6xl px-4 py-10">
          <Routes>
            <Route index element={<Home />} />
            <Route path="search" element={<Registry key="s" />} />
            <Route path="ordinances" element={<Registry key="o" category="ordinance" />} />
            <Route path="resolutions" element={<Registry key="r" category="resolution" />} />
            <Route path="archive" element={<Registry key="a" archived />} />
            <Route path="records/:tn" element={<MeasurePage />} />
            <Route path="track" element={<TrackPage />} />
            <Route path="publication" element={<PublicationPage />} />
            <Route path="sessions" element={<SessionsPage />} />
            <Route path="sessions/:sid" element={<SessionPage />} />
            <Route path="committees" element={<CommitteesPage />} />
            <Route path="notices" element={<NoticesPage />} />
            <Route path="request" element={<RequestPage />} />
          </Routes>
        </main>
        <footer className="border-t border-slate-200 bg-white py-6 text-center text-[11px] text-slate-500">
          {d.lgu.name} · {d.lgu.legislative_body}. Records shown are officially released copies; the signed originals are kept by the {d.portal.records_office}.
          Online display does not by itself satisfy statutory posting or publication requirements.
        </footer>
      </div>
    </Ctx.Provider>
  );
}
